import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { LogicGroupView } from '@/components/logic/LogicGroupView'
import { useConfirm } from '@/components/common/ConfirmDialog'
import { useStageRequirementsByConfiguration, useDeleteRequirement } from '@/features/contract/gating/hooks'
import type { StageRequirement } from '@/features/contract/gating/types'
import { FINANCING_METRIC_LABEL, FINANCING_METRIC_NUMERIC } from '@/features/contract/gating/types'
import { RequirementFormDialog } from './RequirementFormDialog'
import { formatError } from './formatError'

export function StageGatingAccordion({ configurationCode, stageCode, stageLabel }: {
  configurationCode: string
  stageCode: string
  stageLabel: string
}) {
  const confirm = useConfirm()
  const reqs = useStageRequirementsByConfiguration(configurationCode, stageCode)
  const del = useDeleteRequirement()
  const [creating, setCreating] = useState<{ groupNo: number } | null>(null)

  const { groupNos, byGroupNo, nextGroupNo } = useMemo(() => {
    const byGroupNo = new Map<number, StageRequirement[]>()
    for (const r of reqs.data ?? []) {
      const arr = byGroupNo.get(r.groupNo) ?? []
      arr.push(r)
      byGroupNo.set(r.groupNo, arr)
    }
    const groupNos = [...byGroupNo.keys()].sort((a, b) => a - b)
    const nextGroupNo = (groupNos[groupNos.length - 1] ?? 0) + 1
    return { groupNos, byGroupNo, nextGroupNo }
  }, [reqs.data])

  if (reqs.isLoading) return <p className="text-xs text-slate-400 italic px-3 py-2">Ładowanie warunków…</p>

  return (
    <div className="px-3 py-2 border-t bg-slate-50/40">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wide text-slate-500">Warunki przejścia</span>
        <button
          onClick={() => setCreating({ groupNo: groupNos.length === 0 ? 1 : nextGroupNo })}
          className="text-[11px] bg-sky-600 text-white rounded px-2 py-1"
        >+ Dodaj alternatywę (LUB)</button>
      </div>

      {groupNos.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Brak wymagań - bramka otwarta.</p>
      ) : (
        <LogicGroupView<StageRequirement>
          groups={groupNos.map(gn => ({
            id: gn,
            atoms: byGroupNo.get(gn) ?? [],
          }))}
          renderAtom={r => (
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs">
                <RequirementAtomLabel r={r} />
              </div>
              <button
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Usunąć ten warunek?',
                    description: 'Operacja jest nieodwracalna.',
                    confirmLabel: 'Usuń',
                    variant: 'destructive',
                  })
                  if (ok) del.mutate(r.id, { onError: e => toast.error(formatError(e)) })
                }}
                className="text-[10px] text-red-600"
              >Usuń</button>
            </div>
          )}
          renderGroupFooter={(g) => (
            <button
              onClick={() => setCreating({ groupNo: Number(g.id) })}
              className="text-[11px] text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
              title="Dodaj kolejny warunek wymagany razem (I)"
            >
              <span className="rounded bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0 text-[9px] font-semibold">I</span>
              + Dodaj wymagany warunek
            </button>
          )}
        />
      )}

      {creating && (
        <RequirementFormDialog
          configurationCode={configurationCode}
          stageCode={stageCode}
          stageLabel={stageLabel}
          groupNo={creating.groupNo}
          onClose={() => setCreating(null)}
        />
      )}
    </div>
  )
}

function RequirementAtomLabel({ r }: { r: StageRequirement }) {
  switch (r.kind) {
    case 'STATUS':
      return (
        <>
          <span className="rounded bg-sky-100 text-sky-800 px-1.5 py-0.5 text-[10px] mr-2">STATUS</span>
          <span className="font-medium">{r.trackLabelPl}</span>
          <span className="text-slate-400 mx-2">=</span>
          <span>{r.requiredValueLabelPl}</span>
        </>
      )
    case 'DOCUMENT':
      return (
        <>
          <span className="rounded bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] mr-2">DOK</span>
          <span className="font-medium">{r.documentTypeLabelPl ?? r.documentTypeCode}</span>
          <span className="text-slate-400 ml-2">załączony</span>
        </>
      )
    case 'FIELD':
      return (
        <>
          <span className="rounded bg-indigo-100 text-indigo-800 px-1.5 py-0.5 text-[10px] mr-2">POLE</span>
          <span className="font-medium">{r.fieldLabelPl ?? r.fieldCode}</span>
          <span className="text-slate-400 ml-2">wypełnione</span>
        </>
      )
    case 'FIELD_VERIFIED':
      return (
        <>
          <span className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px] mr-2">WERYFIKACJA</span>
          <span className="font-medium">{r.fieldLabelPl ?? r.fieldCode}</span>
          <span className="text-slate-400 ml-2">zweryfikowane</span>
        </>
      )
    case 'FINANCING': {
      const metric = r.financingMetric
      const label = metric ? FINANCING_METRIC_LABEL[metric] : '(metryka?)'
      const numeric = metric ? FINANCING_METRIC_NUMERIC[metric] : false
      return (
        <>
          <span className="rounded bg-rose-100 text-rose-800 px-1.5 py-0.5 text-[10px] mr-2">FINANSE</span>
          <span className="font-medium">{label}</span>
          {numeric && <span className="text-slate-500 ml-2">≥ {r.financingValue}%</span>}
        </>
      )
    }
  }
}
